import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
import * as lz4 from 'lz4-wasm';
import {normalizeCompressedField} from "../lib/compressedField";
export type ConflictUserSignOffs = {userId: uuid, conflict: boolean, partitioned: boolean, ethicalWall: boolean, releaseSigned: boolean, signoffDate: Date, notes: string};
class Conflict extends Base implements EntityType {
    conflictId: uuid;
    userId: uuid;
    checkedContactIds: uuid[];
    userIds: uuid[];
    dateChecked?: Date | null;
    private _report?: string | null | Uint8Array;
    tasksSent: boolean;
    userSignOffs: ConflictUserSignOffs[];
    reportHash?: string | null;
    constructor(doc: typeof Conflict | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            conflictId: genUUID(),
            userId: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "conflict";
        // @ts-ignore
        this.conflictId = doc._conflictId || doc.conflictId || doc.conflictid || genUUID();
        // @ts-ignore
        this.userId = doc._userId || doc.userId || doc.userid || null;
        // @ts-ignore
        this.checkedContactIds = doc._checkedContactIds || doc.checkedContactIds || doc.checkedcontactids || [];
        // @ts-ignore
        this.userIds = doc._userIds || doc.userIds || doc.userids || [];
        // @ts-ignore
        this.dateChecked = DateUtil.timestampOnly(doc._dateChecked) || DateUtil.timestampOnly(doc.dateChecked) || DateUtil.timestampOnly(doc.datechecked) || null;
        // @ts-ignore
        const report = normalizeCompressedField(doc._report || doc.report || doc.report || null);
        if (typeof(report) === 'string') {
            const tmp = lz4.compress(new TextEncoder().encode(report as string));
            if (tmp.length < report.length) this._report = tmp;
            else this._report = report;
        } else this._report = report;
        // @ts-ignore
        this.tasksSent = util.defaultBoolean([doc.tasksSent, doc._tasksSent, doc.taskssent, false]);
        // @ts-ignore
        this.userSignOffs = doc._userSignOffs || doc.userSignOffs || doc.usersignoffs || [];
        // @ts-ignore
        this.reportHash = doc._reportHash || doc.reportHash || doc.reporthash || null;
    }
    get id(): uuid { return this.conflictId; }
    addCheckedContactId(checkedContactId: uuid): uuid {
        if (!checkedContactId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.checkedContactIds) this.checkedContactIds = [];
        this.checkedContactIds.push(checkedContactId);
        return checkedContactId;
    }
    updateCheckedContactId(checkedContactId: uuid) {
        if (!this.checkedContactIds) this.checkedContactIds = [];
        const index = this.checkedContactIds.findIndex(item => item === checkedContactId);
        if (index !== -1) { this.checkedContactIds[index] = checkedContactId; }
        else this.addCheckedContactId(checkedContactId);
    }
    removeCheckedContactId(checkedContactId: uuid) {
        if (!this.checkedContactIds) return;
        let foundItems = this.checkedContactIds?.filter(item => item === checkedContactId);
        if (foundItems && foundItems.length > 0) {
            this.checkedContactIds = this.checkedContactIds.filter(item => !(item === checkedContactId));
        }
    }
    abandonCheckedContactIds() { this.checkedContactIds = []; }
    addUserId(userId: uuid): uuid {
        if (!userId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.userIds) this.userIds = [];
        this.userIds.push(userId);
        return userId;
    }
    updateUserId(userId: uuid) {
        if (!this.userIds) this.userIds = [];
        const index = this.userIds.findIndex(item => item === userId);
        if (index !== -1) { this.userIds[index] = userId; }
        else this.addUserId(userId);
    }
    removeUserId(userId: uuid) {
        if (!this.userIds) return;
        let foundItems = this.userIds?.filter(item => item === userId);
        if (foundItems && foundItems.length > 0) {
            this.userIds = this.userIds.filter(item => !(item === userId));
        }
    }
    abandonUserIds() { this.userIds = []; }
    set report(value) {
        if (this._report !== value) {
            if (!!value) {
                const tmp = lz4.compress(new TextEncoder().encode(value as string));
                if (tmp.length < value.length) this._report = tmp;
                else this._report = value;
            } else this._report = value;
        }
    }
    get report(): string {
        if (!this._report || typeof(this._report) === 'string') return this._report as unknown as string;
        else {
            const decompressed = lz4.decompress(this._report);
            return new TextDecoder('utf-8').decode(decompressed) as string;
        }
    }
    addUserSignOff(userSignOff: ConflictUserSignOffs): ConflictUserSignOffs {
        if (!userSignOff) throw new ValidationError('No nulls allowed in an array!');
        if (!this.userSignOffs) this.userSignOffs = [];
        this.userSignOffs.push(userSignOff);
        return userSignOff;
    }
    updateUserSignOff(userSignOff: ConflictUserSignOffs) {
        if (!this.userSignOffs) this.userSignOffs = [];
        const index = this.userSignOffs.findIndex(item => item === userSignOff);
        if (index !== -1) { this.userSignOffs[index] = userSignOff; }
        else this.addUserSignOff(userSignOff);
    }
    removeUserSignOff(userSignOff: ConflictUserSignOffs) {
        if (!this.userSignOffs) return;
        let foundItems = this.userSignOffs?.filter(item => item === userSignOff);
        if (foundItems && foundItems.length > 0) {
            this.userSignOffs = this.userSignOffs.filter(item => !(item === userSignOff));
        }
    }
    abandonUserSignOffs() { this.userSignOffs = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this conflict"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.conflictId; }
    static override getNonRefArrayFields(): string[] { return ["checkedContactIds", "userIds", "userSignOffs"]; }
    static override getDefaultEntries(): Conflict[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'conflict',
    dataArea: 'Conflicts',
    attachedTo: {parents: ['lead', 'matter'], via: 'entity', onDelete: 'keep', onReplace: 'move'},
    table: 'CONFLICT',
    comment: `A conflict-of-interest check record for a lead or matter. Each check captures a snapshot of the firm's attorneys at the time, a generated report of potential conflicts, and per-attorney sign-offs indicating whether a conflict exists and how it is being managed (ethical wall or client release).`,
    fields: {

        conflictId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this conflict'
        },

        /**
         * The ID of the person that initiated the check
         */
        userId: {
            type: 'uuid',
            entityType: 'user',
            deleteStrategy: 'preserve',
            required: true,
            comment: 'The user running the conflict check'
        },

        /**
         * A list of contactIds of people we checked in this run.
         */
        checkedContactIds: {
            type: 'uuid[]',
            entityType: 'contact',
            comment: 'Contacts included in this check'
        },

        /**
         * A list of userIds of people at the firm at the time of this check.
         */
        userIds: {
            type: 'uuid[]',
            entityType: 'user',
            comment: 'The users at the firm at the time of this check (a snapshot of firm membership)'
        },

        /**
         * The date and time the check was run
         */
        dateChecked: {
            type: 'timestamp',
            comment: 'The date and time the conflict check was run'
        },

        /**
         * A generated, formatted report showing what conflicts were found
         */
        report: {
            type: 'string',
            compressed: true,
            comment: 'A formatted report for this conflict check'
        },

        /**
         * Have we sent out tasks to each user asking them to review this conflict report?
         */
        tasksSent: {
            type: 'boolean',
            comment: 'Have we sent out the tasks yet?'
        },

        /**
         * Array of responses from users.  Each response has the following attributes:
         *
         * userId: ID of the user signing off
         * conflict: is there a conflict?
         * partitioned: is this user being partitioned from the case?
         * ethicalWall: is an ethical wall being used for this case/user?
         * signoffDate: the date/time the user signed off
         * notes: any notes from the user about a potential conflict.
         */
        userSignOffs: {
            type: 'json[]',
            required: false,
            jsdoc: '{userId: uuid, conflict: boolean, partitioned: boolean, ethicalWall: boolean, releaseSigned: boolean, signoffDate: Date, notes: string}',
            entity: {
                type: 'user',
                value: 'userId',
                deleteStrategy: 'preserve' // always keep for historical records
            },
            comment: 'Array of responses from users'
        },

        /**
         * OWA-1372: SHA-256 (64 lowercase hex characters) of the report's HTML, which is stored in
         * R2 under {firmId}/conflict-reports/{conflictId}/{reportHash}.html. The client computes it
         * and uploads the report before saving; a save without it keeps the stored one.
         */
        reportHash: {
            type: 'string',
            minLength: 64,
            maxLength: 64,
            keptWhenOmitted: true,
            comment: 'SHA-256 of the report HTML stored in R2 (OWA-1372)'
        }
    },
    maps: [
        {name: 'userId', keysFn: t => t.userId, presentFn: (t, k) => t.userId === k},
        {name: 'checkedContactIds', keysFn: t => t.checkedContactIds, presentFn: (t, k) => t.checkedContactIds?.includes(k)},
        {name: 'userIds', keysFn: t => t.userIds, presentFn: (t, k) => t.userIds?.includes(k)}
    ],
    /**
     * OWA-1372: a report hash names an object in R2, so it is 64 lowercase hex characters (the
     * upload route and the read route accept nothing else). No hash is allowed: rows the TS server
     * wrote have none until the backfill.
     */
    invariants: [{
        message: 'The report hash must be 64 lowercase hexadecimal characters.',
        fn: t => t.reportHash == null || t.reportHash === '' || /^[0-9a-f]{64}$/.test(t.reportHash)
    }]
});
    }
}
export default Conflict;
